import React from 'react';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import throttle from 'lodash/throttle';
import axios from 'axios';

import { Box } from '@origin-digital/layout';
import { Text, TextField, Spinner, SelectField, BREAK_POINTS } from '@origin-digital/style-guide';
import { AddressLookup } from '@origin-digital/address-lookup';
import { onlyNumeric } from '@origin-digital/normalize';

import { IWizardStepProps, WizardContext } from '../../Wizard';
import {
  Container,
  Content,
  Actions,
  Heading,
  ContentText,
  InlineSelect,
  LoadingOverlay,
} from '../common';
import { isSolarAvailable, isEligible } from '../utils';
import { isFeatureEnabled } from '@origin-digital/features';

interface IAddressState {
  loading?: boolean;
  hasInput: boolean;
}

interface IAddressProps extends IWizardStepProps {
  addressApiProps: object;
  addressReverseLookupApiEndPoint: string;
  plansApiEndPoint: string;
}

const Postcode = styled.div`
  @media screen and (min-width: ${BREAK_POINTS.Min.MD}) {
    width: 225px;
  }
  @media screen and (min-width: ${BREAK_POINTS.Min.LG}) {
    width: 270px;
  }
`;

export class Address extends React.Component<IAddressProps, IAddressState> {
  static contextType = WizardContext;
  public state = {
    hasInput: false,
    loading: false,
  };

  handleAddressInput = (address) => {
    if (address && !isEqual(address, this.context.address)) {
      this.checkPlans({ address } as any);
    }

    if (!this.state.hasInput) {
      this.setState({
        hasInput: true,
      });
    }
  };

  handleAddressSelect = (address) => {
    this.setState({ loading: true });
  };

  handlePostcode = (event) => {
    const postcode = event.target.value;
    this.context.saveAnswers({ postcode, plans: undefined });

    if (postcode.length === 4) {
      this.checkPlans({ postcode } as any);
    }
  };

  handleEntryType = (event) => {
    const entryType = event.target.value;
    this.context.saveAnswers({
      usePostcode: entryType === 'postcode',
      address: undefined,
      postcode: undefined,
      plans: undefined,
    });
  };

  handleAddressError = () => {
    this.setState({ loading: false });
  };

  checkPlans = throttle(({ address, postcode }) => {
    const useCache = isFeatureEnabled(this.props.featureContext, 'sales-core-cache-cp');
    const { plansApiEndPoint } = this.props;
    this.setState({ loading: true });
    const addressId = get(address, 'sapAddressId');
    const { answers } = this.context;

    return axios
      .get(plansApiEndPoint, {
        params: {
          addressId,
          connectionScenario: 'PROS_SWT',
          customerType: 'residential',
          fuelType: 'dual',
          hasSolarPanels: true,
          postcode,
          searchByPostcode: !!postcode,
          useCache
        },
      })
      .then((response) => {
        const { plans, fuelEligibilityStatus } = response.data.data;
        this.context.saveAnswers({
          plans,
          fuelEligibilityStatus,
          electricity: isEligible(fuelEligibilityStatus.electricity) ? answers.electricity : false,
          gas: isEligible(fuelEligibilityStatus.gas) ? answers.gas : false,
          solar: false,
          solarAvailable: isSolarAvailable(plans),
          address,
          postcode,
          usePostcode: !!postcode,
          useCache
        });
      })
      .catch((...args) => {
        this.context.saveAnswers({
          postcode,
          address,
          plans: undefined,
        });
        return Promise.reject(...args);
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }, 200);

  render() {
    const { loading } = this.state;
    const { addressApiProps, addressReverseLookupApiEndPoint } = this.props;
    const { qasAuthToken, apiKey, url } = addressApiProps as any;
    const { address, postcode, usePostcode } = this.context.answers;

    return (
      <Container>
        <Content>
          <Heading>Let’s start with your {usePostcode ? 'postcode' : 'address'}</Heading>
          <ContentText>
            For more accurate pricing, enter your address. We recommend this because rates are based
            on your distributor and meter type.
            <br />
          </ContentText>
          <ContentText>
            <Text inline fontWeight="medium">
              Search energy plans by
            </Text>
            &nbsp;
            <InlineSelect>
              <SelectField
                name="address-or-postcode"
                label={null}
                prompt={false}
                value={usePostcode ? 'postcode' : 'address'}
                options={[
                  { label: 'address', value: 'address' },
                  { label: 'postcode', value: 'postcode' },
                ]}
                onChange={this.handleEntryType}
              />
            </InlineSelect>
          </ContentText>

          <Box width="100%" maxWidth="568px">
            {usePostcode ? (
              <Postcode>
                <TextField
                  id="postcode-input"
                  label="Your postcode"
                  data-id="postcode-entry"
                  value={postcode || ''}
                  maxLength={4}
                  transform={onlyNumeric}
                  onChange={this.handlePostcode}
                  autoFocus
                  spinner={loading}
                />
              </Postcode>
            ) : (
              <AddressLookup
                label="Enter your address"
                addressTranslationApiEndPoint={url}
                addressReverseLookupApiEndPoint={addressReverseLookupApiEndPoint}
                qasAuthToken={qasAuthToken}
                addressTranslationApiKey={apiKey}
                onAddressSelect={this.handleAddressSelect}
                onChange={this.handleAddressInput}
                onError={this.handleAddressError}
                value={address}
                disableSapAddressIdLookup={!this.state.hasInput}
              />
            )}
            {loading && (
              <LoadingOverlay>
                <Spinner size="lg" />
              </LoadingOverlay>
            )}
            <Actions {...this.props} />
          </Box>
        </Content>
      </Container>
    );
  }
}
